import { useEffect } from 'react';
import {
  useConversations,
  useSelectedConversation,
  SelectedConversationProvider,
  ChatLoadingState,
  ChatErrorState,
  ChatEmptyState,
  ConversationList,
  ConversationView,
} from '../components/chat';
import { useAskForPushPermission } from '../lib/usePushPermissionAsk';

function ChatContent() {
  const { conversations, userCache, userId, isLoading, error } = useConversations();
  const {
    selectedConversation,
    messages,
    pendingMessages,
    messageText,
    newestMessageId,
    setMessageText,
    selectConversation,
    sendMessage,
    retryMessage,
    dismissFailedMessage,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
  } = useSelectedConversation();

  // Push notifications. The `hasRequestedPermission` ref this used to carry
  // only guarded against a re-render of this one component; the shared hook
  // guards against every other screen that asks too. The old timer was also
  // returned from inside a `.then`, where React never saw it, so navigating
  // away between the check and the ask still raised the sheet.
  const askForPush = useAskForPushPermission();

  useEffect(() => {
    const tid = setTimeout(() => void askForPush(), 1000);
    return () => clearTimeout(tid);
  }, [askForPush]);

  // Loading state
  if (isLoading) {
    return <ChatLoadingState />;
  }

  // Error state
  if (error) {
    return <ChatErrorState error={error} />;
  }

  // Empty state
  if (conversations.length === 0 && !selectedConversation) {
    return <ChatEmptyState />;
  }

  return (
    // `min-h-0` is what stops this column from being sized by its content: the
    // route wrapper hands it a definite height (see `routeOwnsViewport`), and
    // the panes inside divide that up rather than adding to it.
    <div className="w-full flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
      <ConversationList
        conversations={conversations}
        selectedConversation={selectedConversation}
        userId={userId}
        userCache={userCache}
        onSelect={selectConversation}
      />

      <ConversationView
        conversation={selectedConversation}
        messages={messages}
        pendingMessages={pendingMessages}
        userId={userId}
        userCache={userCache}
        messageText={messageText}
        newestMessageId={newestMessageId}
        onMessageTextChange={setMessageText}
        onSendMessage={sendMessage}
        onRetryMessage={retryMessage}
        onDismissMessage={dismissFailedMessage}
        onBack={() => selectConversation(null)}
        onLoadMore={loadMoreMessages}
        hasMoreMessages={hasMoreMessages}
        isLoadingMore={isLoadingMore}
      />
    </div>
  );
}

export default function Chat() {
  return (
    <SelectedConversationProvider>
      <ChatContent />
    </SelectedConversationProvider>
  );
}
